'use client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { TCardFacet, TFacet, TFacetValue } from '@/types/collection'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'
import { FilterValues } from '../../../types/filter-store-v2.types'
import { shouldAddNofollowToFilterLinks } from '../../../utils/filter-utils'
import { buildFilterUrl } from './filter-url-builder'

type Props = {
  selectedValues: string[]
  onFilterChange: (values: string[]) => void
  attribute: {
    title: string
    seo_alias: string
    items: TFacetValue[]
    is_allow_multiple?: boolean
    is_concat_slug?: boolean
  }
  originalSlug?: string
  currentFilters?: FilterValues
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
}

const ITEMS_LIMIT = 8

export const FilterCheckbox: React.FC<Props> = ({
  selectedValues,
  onFilterChange,
  attribute,
  originalSlug,
  currentFilters = {},
  facets,
  cardFacets,
}) => {
  const [showAll, setShowAll] = useState(false)
  const t = useTranslations('collection_page')

  // Normalize selectedValues to lowercase array for consistent comparison
  const normalizedSelectedValues = React.useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) return []
    const normalized = selectedValues
      .map((s) => String(s).trim().toLowerCase())
      .filter((s) => s.length > 0)

    return normalized
  }, [selectedValues])

  const handleSelect = (value: string) => {
    const valueLower = value.toLowerCase()
    const newValues = normalizedSelectedValues.includes(valueLower)
      ? normalizedSelectedValues.filter((v) => v !== valueLower)
      : [...normalizedSelectedValues, valueLower]
    onFilterChange(newValues)
  }

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    value: string
  ) => {
    e.preventDefault()
    handleSelect(value)
  }

  const shouldShowToggle = attribute.items.length > ITEMS_LIMIT
  const displayItems = showAll
    ? attribute.items
    : attribute.items.slice(0, ITEMS_LIMIT)

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    currentFilters,
    facets,
    cardFacets
  )

  // Build URL for each filter option
  const getFilterUrl = (item: TFacetValue): string => {
    if (!originalSlug) return '#'
    const itemLower = item.seo_alias.toLowerCase()
    const isChecked = normalizedSelectedValues.includes(itemLower)
    return buildFilterUrl({
      originalSlug,
      currentFilters,
      facetKey: attribute.seo_alias,
      facetValue: item.seo_alias,
      isChecked: !isChecked, // URL for toggled state
      isMultiple: attribute.is_allow_multiple ?? true,
      isConcatSlug: attribute.is_concat_slug ?? false,
      facets,
      cardFacets,
    })
  }

  return (
    <div className='flex flex-col gap-4.5'>
      {displayItems.map((item) => {
        const itemLower = item.seo_alias.toLowerCase()
        const isChecked = normalizedSelectedValues.includes(itemLower)
        const filterUrl = getFilterUrl(item)

        return (
          <div key={item.seo_alias} className='flex items-center gap-2'>
            <Checkbox
              id={item.seo_alias}
              checked={isChecked}
              onCheckedChange={() => handleSelect(item.seo_alias)}
              title={
                <a
                  href={filterUrl}
                  onClick={(e) => handleLinkClick(e, item.seo_alias)}
                  className='[display:contents]'
                  aria-label={`Filter by ${item.title}`}
                  rel={shouldAddNofollow ? 'nofollow' : undefined}
                >
                  {item.title}
                </a>
              }
            />
          </div>
        )
      })}

      {shouldShowToggle && (
        <Button
          variant='ghost'
          size='small'
          onClick={() => setShowAll(!showAll)}
          className='w-full justify-start text-xs text-neutral-600 hover:text-neutral-900'
        >
          {showAll ? t('button.show_less') : t('button.show_more')}
          {!showAll && ` (${attribute.items.length - ITEMS_LIMIT} )`}
        </Button>
      )}
    </div>
  )
}
